<template>
  <div id="app">
    <!-- Header avec dégradé violet/bleu -->
    <header class="app-header">
      <div class="header-content">
        <h1>Body Metrics - Suivi des Données Corporelles</h1>
        <UserSelector
          :users="users"
          :current-user="currentUser"
          @user-changed="handleUserChange"
        />
      </div>

      <!-- Navigation à onglets -->
      <div class="tabs-navigation">
        <button
          :class="['tab-button', { active: currentTab === 'measurements' }]"
          @click="currentTab = 'measurements'"
        >
          📊 Mesures
        </button>
        <button
          :class="['tab-button', { active: currentTab === 'goals' }]"
          @click="currentTab = 'goals'"
        >
          🎯 Objectifs
        </button>
        <button
          :class="['tab-button', { active: currentTab === 'statistics' }]"
          @click="currentTab = 'statistics'"
        >
          📈 Statistiques
        </button>
      </div>
    </header>

    <main class="main-content">
      <div class="container">
        <div v-if="isLoading" class="loading">
          <p>Chargement des données...</p>
        </div>

        <div v-else>
          <!-- Vue Mesures -->
          <div v-if="currentTab === 'measurements'" class="tab-content">
            <!-- Tableau d'affichage -->
            <section class="table-section">
              <h2>Historique des Mesures</h2>
              <DataTable
                :measurements="currentUserMeasurements"
                @measurement-updated="handleMeasurementUpdated"
                @measurement-deleted="handleMeasurementDeleted"
              />
            </section>

            <!-- Formulaire de saisie -->
            <section class="form-section">
              <h2>Nouvelle Mesure</h2>
              <DataForm
                :current-user="currentUser"
                @measurement-added="handleMeasurementAdded"
              />
            </section>
          </div>

          <!-- Vue Objectifs -->
          <div v-else-if="currentTab === 'goals'" class="tab-content">
            <GoalView
              :current-user="currentUser"
              :user-data="currentUserData"
              @goals-updated="loadUsersData"
            />
          </div>

          <!-- Vue Statistiques -->
          <div v-else-if="currentTab === 'statistics'" class="tab-content">
            <section class="statistics-section">
              <h2>Statistiques</h2>
              <Statistics
                :measurements="currentUserMeasurements"
                :current-user="currentUser"
              />
            </section>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import UserSelector from './components/UserSelector.vue'
import DataForm from './components/DataForm.vue'
import DataTable from './components/DataTable.vue'
import Statistics from './components/Statistics.vue'
import GoalView from './components/GoalView.vue'
import { bodyMetricsAPI } from './services/api.js'

export default {
  name: 'App',
  components: {
    UserSelector,
    DataForm,
    DataTable,
    Statistics,
    GoalView
  },
  setup() {
    // État réactif
    const users = ref([])
    const currentUser = ref('chikyto')
    const currentTab = ref('measurements')
    const isLoading = ref(true)
    
    // Données calculées
    const currentUserData = computed(() => {
      return users.value.find(user => user.id === currentUser.value)
    })
    
    const currentUserMeasurements = computed(() => {
      const userData = currentUserData.value
      if (!userData || !userData.data.measurements) return []
      
      // Tri par date (plus récent en premier)
      return [...userData.data.measurements].sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.time)
        const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.time)
        return dateB - dateA
      })
    })

    // Chargement des données via API
    const loadUsersData = async () => {
      try {
        console.log('Chargement des données via API...')
        const usersData = await bodyMetricsAPI.getUsers()
        users.value = usersData
        isLoading.value = false
        console.log('Données chargées avec succès:', usersData)
      } catch (error) {
        console.error('Erreur lors du chargement des données via API:', error)
        // Fallback vers localStorage en cas d'erreur API
        console.log('Tentative de chargement depuis localStorage...')
        loadFromLocalStorage()
        isLoading.value = false
      }
    }

    // Méthodes
    const handleUserChange = (userId) => {
      currentUser.value = userId
      saveToLocalStorage()
    }


    const handleMeasurementAdded = async (measurement) => {
      try {
        const result = await bodyMetricsAPI.addMeasurement(currentUser.value, measurement)
        if (result.success) {
          // Recharger les données pour synchroniser avec le serveur
          await loadUsersData()
          console.log('Mesure ajoutée avec succès')
        }
      } catch (error) {
        console.error('Erreur lors de l\'ajout de la mesure:', error)
        // Fallback vers localStorage
        const userData = currentUserData.value
        if (userData) {
          measurement.id = `measurement_${Date.now()}`
          userData.data.measurements.push(measurement)
          saveToLocalStorage()
        }
      }
    }

    const handleMeasurementUpdated = async (updatedMeasurement) => {
      try {
        const result = await bodyMetricsAPI.updateMeasurement(currentUser.value, updatedMeasurement.id, updatedMeasurement)
        if (result.success) {
          // Recharger les données pour synchroniser avec le serveur
          await loadUsersData()
          console.log('Mesure mise à jour avec succès')
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la mesure:', error)
        // Fallback vers localStorage
        const userData = currentUserData.value
        if (userData) {
          const index = userData.data.measurements.findIndex(m => m.id === updatedMeasurement.id)
          if (index !== -1) {
            userData.data.measurements[index] = updatedMeasurement
            saveToLocalStorage()
          }
        }
      }
    }

    const handleMeasurementDeleted = async (measurementId) => {
      try {
        const result = await bodyMetricsAPI.deleteMeasurement(currentUser.value, measurementId)
        if (result.success) {
          // Recharger les données pour synchroniser avec le serveur
          await loadUsersData()
          console.log('Mesure supprimée avec succès')
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la mesure:', error)
        // Fallback vers localStorage
        const userData = currentUserData.value
        if (userData) {
          userData.data.measurements = userData.data.measurements.filter(m => m.id !== measurementId)
          saveToLocalStorage()
        }
      }
    }

    const saveToLocalStorage = () => {
      // Simulation de sauvegarde (en production, ce serait des appels API)
      localStorage.setItem('bodyMetricsUsers', JSON.stringify(users.value))
      localStorage.setItem('bodyMetricsCurrentUser', currentUser.value)
    }

    const loadFromLocalStorage = () => {
      const savedUsers = localStorage.getItem('bodyMetricsUsers')
      const savedCurrentUser = localStorage.getItem('bodyMetricsCurrentUser')
      
      if (savedUsers) {
        users.value = JSON.parse(savedUsers)
      }
      
      if (savedCurrentUser) {
        currentUser.value = savedCurrentUser
      }
    }

    // Lifecycle
    onMounted(async () => {
      await loadUsersData()
      // Note: loadFromLocalStorage() est déjà appelé comme fallback dans loadUsersData() en cas d'erreur API
      // Ne pas le rappeler ici sinon ça écrase les données fraîches de l'API
    })

    return {
      users,
      currentUser,
      currentTab,
      currentUserData,
      currentUserMeasurements,
      isLoading,
      loadUsersData,
      handleUserChange,
      handleMeasurementAdded,
      handleMeasurementUpdated,
      handleMeasurementDeleted
    }
  }
}
</script>

<style>
/* Reset et styles globaux */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Header avec dégradé */
.app-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content h1 {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
}

/* Navigation à onglets */
.tabs-navigation {
  max-width: 1200px;
  margin: 1rem auto 0;
  padding: 0 2rem;
  display: flex;
  gap: 1rem;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

.tab-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
}

.tab-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.tab-button.active {
  color: white;
  border-bottom-color: white;
  background: rgba(255, 255, 255, 0.15);
}

/* Contenu principal */
.main-content {
  padding: 2rem 0;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-size: 1.1rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.form-section,
.table-section,
.statistics-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.form-section h2,
.table-section h2,
.statistics-section h2 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.tab-content {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }

  .header-content h1 {
    font-size: 1.5rem;
  }

  .tabs-navigation {
    gap: 0.25rem;
    padding: 0 1rem;
  }

  .tab-button {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .container {
    padding: 0 1rem;
  }

  .form-section,
  .table-section,
  .statistics-section {
    padding: 1.5rem;
  }
}
</style>
